import { Component, CSP_NONCE, inject, OnDestroy, OnInit } from '@angular/core';
import { Language, UserSettings } from '@auth/login/data/login.model';
import { TranslateService } from '@ngx-translate/core';
import { isBrowserInGerman } from '@shared/components/header/menu-footer/menu-footer.component';
import { SessionFacade } from '@shared/store/session.facade';
import { environment } from 'environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

interface CookieCategory {
  level: number;
  titleKey: string;
  descriptionKey: string;
  bulletKeys: string[];
  required: boolean;
  accepted: boolean;
}

export const COOKIE_NAME = 'dws01-level';

@Component({
  selector: 'eop-cookie-banner',
  templateUrl: './cookie-banner.component.html',
  styleUrls: ['./cookie-banner.component.scss'],
  standalone: false,
})
export class CookieBannerComponent implements OnInit, OnDestroy {
  protected visible = false;
  protected showDetails = false;
  protected dataProtectionUrl = environment.dataProtectionUrlEN;
  protected readonly categories: CookieCategory[] = [
    {
      level: 1,
      titleKey: 'COOKIE_BANNER.REQUIRED_TITLE',
      descriptionKey: 'COOKIE_BANNER.REQUIRED_DESCRIPTION',
      bulletKeys: [
        'COOKIE_BANNER.REQUIRED_BULLET_1',
        'COOKIE_BANNER.REQUIRED_BULLET_2',
        'COOKIE_BANNER.REQUIRED_BULLET_3',
        'COOKIE_BANNER.REQUIRED_BULLET_4',
      ],
      required: true,
      accepted: true,
    },
    {
      level: 2,
      titleKey: 'COOKIE_BANNER.ANALYTICAL_TITLE',
      descriptionKey: 'COOKIE_BANNER.ANALYTICAL_DESCRIPTION',
      bulletKeys: ['COOKIE_BANNER.ANALYTICAL_BULLET_1'],
      required: false,
      accepted: false,
    },
  ];

  private readonly cspNonce = inject(CSP_NONCE, { optional: true });
  private readonly cookieService = inject(CookieService);
  private readonly sessionFacade = inject(SessionFacade);
  private readonly translateService = inject(TranslateService);
  private readonly unsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    const hasConsent = this.cookieService.check(COOKIE_NAME);
    if (hasConsent && Number(this.cookieService.get(COOKIE_NAME)) >= 2) {
      this.loadAppDynamics();
    }
    if (!hasConsent) {
      if (this.translateService.currentLang) {
        this.visible = true;
      } else {
        this.translateService.onLangChange
          .pipe(take(1), takeUntil(this.unsubscribe$))
          .subscribe(event => {
            this.visible = !!event?.lang;
          });
      }
    }
    this.sessionFacade.userSettings$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((settings: UserSettings) => {
        const locale = (settings as UserSettings)?.language?.toLocaleLowerCase() as Language;
        const isGerman = locale ? locale === Language.German : isBrowserInGerman();
        this.dataProtectionUrl = isGerman
          ? environment.dataProtectionUrlDE
          : environment.dataProtectionUrlEN;
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  acceptAll(): void {
    this.setLevel(2);
  }

  acceptSelected(): void {
    const maxAccepted = this.categories
      .filter(c => c.accepted)
      .reduce((max, c) => Math.max(max, c.level), 1);
    this.setLevel(maxAccepted);
  }

  toggleCategory(category: CookieCategory): void {
    if (category.required) return;
    category.accepted = !category.accepted;
  }

  private setLevel(level: number): void {
    const days = level === 1 ? 1 : level === 2 ? 730 : 90;
    const expires = new Date();
    expires.setDate(expires.getDate() + days);
    this.cookieService.set(COOKIE_NAME, String(level), {
      expires,
      path: '/',
      secure: true,
      sameSite: 'Lax',
    });
    sessionStorage.setItem('clb', 'true');
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      event: [
        {
          eventInfo: {
            eventAction: 'clb_accepted',
            eventCategory: 'CLB Accepted',
            eventLabel: `CLB_Accept (Set Level ${level})`,
          },
        },
      ],
    });
    if (level >= 2) {
      this.loadAppDynamics();
    }
    this.visible = false;
  }

  private loadAppDynamics(): void {
    if ((window as any)['adrum-start-time']) return;
    const config = environment.appDynamics;
    if (!config) return;
    (window as any)['adrum-config'] = Object.assign({}, config);
    (window as any)['adrum-start-time'] = new Date().getTime();
    const script = document.createElement('script');
    script.src = '//cdn.appdynamics.com/adrum/adrum-4.5.4.1467.js';
    if (this.cspNonce) {
      script.setAttribute('nonce', this.cspNonce);
    }
    document.head.appendChild(script);
  }
}
